import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';

const AddHealthRecordModal = ({ clientId, open, onClose }) => {
  const [record, setRecord] = useState({
    peso: '',
    glucemia: '',
    colesterol: '',
    colesterol_hdl: '',
    colesterol_ldl: '',
    trigliceridos: '',
    circunferencia_cintura: '',
    circunferencia_cadera: '',
    circunferencia_pecho: '',
    circunferencia_brazo: '',
    circunferencia_muslo: '',
    porcentaje_grasa: '',
    masa_muscular: '',
    indice_visceral: '',
    tension_sistolica: '',
    tension_diastolica: '',
    pulso: '',
    spo2: '',
    fecha: '',
    notas: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const api = createAuthAPI();
      const data = {};
      
      // Only include non-empty fields
      Object.keys(record).forEach(key => {
        if (record[key] !== '') {
          if (key === 'notas' || key === 'fecha') {
            data[key] = record[key];
          } else {
            data[key] = parseFloat(record[key]);
          }
        }
      });
      
      if (!data.peso) {
        toast.error('El peso es obligatorio');
        return;
      }
      
      await api.post(`/admin/clients/${clientId}/weight-record`, data);
      toast.success('Registro añadido exitosamente');
      
      // Reset form
      setRecord({
        peso: '',
        glucemia: '',
        colesterol: '',
        colesterol_hdl: '',
        colesterol_ldl: '',
        trigliceridos: '',
        circunferencia_cintura: '',
        circunferencia_cadera: '',
        circunferencia_pecho: '',
        circunferencia_brazo: '',
        circunferencia_muslo: '',
        porcentaje_grasa: '',
        masa_muscular: '',
        indice_visceral: '',
        tension_sistolica: '',
        tension_diastolica: '',
        pulso: '',
        spo2: '',
        fecha: '',
        notas: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error adding record:', error);
      toast.error(error.response?.data?.detail || 'Error al añadir registro');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-800">
            Nuevo Registro de Mediciones
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Fecha del Registro */}
          <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-4 text-lg">📅 Fecha del Registro</h3>
            <div className="space-y-2">
              <Label htmlFor="fecha" className="text-base font-semibold">Fecha (opcional - por defecto hoy)</Label>
              <Input
                id="fecha"
                type="date"
                value={record.fecha}
                onChange={(e) => setRecord({...record, fecha: e.target.value})}
                className="text-lg py-6 border-2"
              />
              <p className="text-sm text-gray-600 italic">
                💡 Deja vacío para usar la fecha actual, o selecciona una fecha anterior para añadir registros históricos
              </p>
            </div>
          </div>

          {/* Peso - Obligatorio */}
          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
            <h3 className="font-semibold text-green-800 mb-4 text-lg">⚖️ Peso (Obligatorio)</h3>
            <div className="space-y-2">
              <Label htmlFor="peso" className="text-base font-semibold">Peso (kg) *</Label>
              <Input
                id="peso"
                type="number"
                step="0.1"
                value={record.peso}
                onChange={(e) => setRecord({...record, peso: e.target.value})}
                required
                className="text-lg py-6 border-2"
                placeholder="Ej: 75.5"
              />
            </div>
          </div>

          {/* Análisis de Sangre */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-4 text-lg">🩸 Análisis de Sangre</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="glucemia" className="text-base font-semibold">Glucemia (mg/dL)</Label>
                <Input
                  id="glucemia"
                  type="number"
                  step="0.1"
                  value={record.glucemia}
                  onChange={(e) => setRecord({...record, glucemia: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 95"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colesterol" className="text-base font-semibold">Colesterol Total (mg/dL)</Label>
                <Input
                  id="colesterol"
                  type="number"
                  step="0.1"
                  value={record.colesterol}
                  onChange={(e) => setRecord({...record, colesterol: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colesterol_hdl" className="text-base font-semibold">Colesterol HDL (mg/dL)</Label>
                <Input
                  id="colesterol_hdl"
                  type="number"
                  step="0.1"
                  value={record.colesterol_hdl}
                  onChange={(e) => setRecord({...record, colesterol_hdl: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colesterol_ldl" className="text-base font-semibold">Colesterol LDL (mg/dL)</Label>
                <Input
                  id="colesterol_ldl"
                  type="number"
                  step="0.1"
                  value={record.colesterol_ldl}
                  onChange={(e) => setRecord({...record, colesterol_ldl: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 130"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trigliceridos" className="text-base font-semibold">Triglicéridos (mg/dL)</Label>
                <Input
                  id="trigliceridos"
                  type="number"
                  step="0.1"
                  value={record.trigliceridos}
                  onChange={(e) => setRecord({...record, trigliceridos: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 150"
                />
              </div>
            </div>
          </div>

          {/* Medidas Corporales */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-4 text-lg">📏 Medidas Corporales (cm)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="circunferencia_cintura" className="text-base font-semibold">Circunferencia Cintura</Label>
                <Input
                  id="circunferencia_cintura"
                  type="number"
                  step="0.1"
                  value={record.circunferencia_cintura}
                  onChange={(e) => setRecord({...record, circunferencia_cintura: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 85"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="circunferencia_cadera" className="text-base font-semibold">Circunferencia Cadera</Label>
                <Input
                  id="circunferencia_cadera"
                  type="number"
                  step="0.1"
                  value={record.circunferencia_cadera}
                  onChange={(e) => setRecord({...record, circunferencia_cadera: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="circunferencia_pecho" className="text-base font-semibold">Circunferencia Pecho</Label>
                <Input
                  id="circunferencia_pecho"
                  type="number"
                  step="0.1"
                  value={record.circunferencia_pecho}
                  onChange={(e) => setRecord({...record, circunferencia_pecho: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 95"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="circunferencia_brazo" className="text-base font-semibold">Circunferencia Brazo</Label>
                <Input
                  id="circunferencia_brazo"
                  type="number"
                  step="0.1"
                  value={record.circunferencia_brazo}
                  onChange={(e) => setRecord({...record, circunferencia_brazo: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="circunferencia_muslo" className="text-base font-semibold">Circunferencia Muslo</Label>
                <Input
                  id="circunferencia_muslo"
                  type="number"
                  step="0.1"
                  value={record.circunferencia_muslo}
                  onChange={(e) => setRecord({...record, circunferencia_muslo: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 55"
                />
              </div>
            </div>
          </div>

          {/* Composición Corporal */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-semibold text-purple-800 mb-4 text-lg">💪 Composición Corporal</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="porcentaje_grasa" className="text-base font-semibold">Porcentaje de Grasa (%)</Label>
                <Input
                  id="porcentaje_grasa"
                  type="number"
                  step="0.1"
                  value={record.porcentaje_grasa}
                  onChange={(e) => setRecord({...record, porcentaje_grasa: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 25.5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="masa_muscular" className="text-base font-semibold">Masa Muscular (kg)</Label>
                <Input
                  id="masa_muscular"
                  type="number"
                  step="0.1"
                  value={record.masa_muscular}
                  onChange={(e) => setRecord({...record, masa_muscular: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 45.2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="indice_visceral" className="text-base font-semibold">Índice Visceral</Label>
                <Input
                  id="indice_visceral"
                  type="number"
                  step="0.1"
                  value={record.indice_visceral}
                  onChange={(e) => setRecord({...record, indice_visceral: e.target.value})}
                  className="text-base py-5 border-2"
                  placeholder="Ej: 12"
                />
              </div>
            </div>
          </div>

          {/* Signos Vitales */}
          <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
            <h3 className="font-semibold text-cyan-800 mb-4 text-lg">❤️ Signos Vitales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tension_sistolica" className="text-base font-semibold">
                  Tensión Arterial Sistólica (mmHg)
                </Label>
                <Input
                  id="tension_sistolica"
                  type="number"
                  step="1"
                  value={record.tension_sistolica}
                  onChange={(e) => setRecord({...record, tension_sistolica: e.target.value})}
                  className="text-base py-6 border-2"
                  placeholder="Ej: 120"
                />
                <p className="text-xs text-gray-600">📊 Normal: 90-120 mmHg</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tension_diastolica" className="text-base font-semibold">
                  Tensión Arterial Diastólica (mmHg)
                </Label>
                <Input
                  id="tension_diastolica"
                  type="number"
                  step="1"
                  value={record.tension_diastolica}
                  onChange={(e) => setRecord({...record, tension_diastolica: e.target.value})}
                  className="text-base py-6 border-2"
                  placeholder="Ej: 80"
                />
                <p className="text-xs text-gray-600">📊 Normal: 60-80 mmHg</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pulso" className="text-base font-semibold">
                  Pulso / Frecuencia Cardíaca (lpm)
                </Label>
                <Input
                  id="pulso"
                  type="number"
                  step="1"
                  value={record.pulso}
                  onChange={(e) => setRecord({...record, pulso: e.target.value})}
                  className="text-base py-6 border-2"
                  placeholder="Ej: 75"
                />
                <p className="text-xs text-gray-600">📊 Normal: 60-100 latidos/min</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="spo2" className="text-base font-semibold">
                  Saturación de Oxígeno SpO2 (%)
                </Label>
                <Input
                  id="spo2"
                  type="number"
                  step="0.1"
                  value={record.spo2}
                  onChange={(e) => setRecord({...record, spo2: e.target.value})}
                  className="text-base py-6 border-2"
                  placeholder="Ej: 98"
                />
                <p className="text-xs text-gray-600">📊 Normal: 95-100%</p>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="bg-amber-50 p-4 rounded-lg">
            <h3 className="font-semibold text-amber-800 mb-4 text-lg">📝 Notas</h3>
            <div className="space-y-2">
              <Label htmlFor="notas" className="text-base font-semibold">Observaciones</Label>
              <Textarea
                id="notas"
                value={record.notas}
                onChange={(e) => setRecord({...record, notas: e.target.value})}
                className="text-base border-2"
                rows={4}
                placeholder="Observaciones adicionales sobre la visita..."
              />
            </div>
          </div>

          <Button type="submit" className="w-full pharmacy-button text-white py-6 text-lg font-semibold">
            💾 Guardar Registro Completo
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddHealthRecordModal;
